// DesignCard — the foundational card used in My Designs, Quick Reorder,
// and detail panel previews. Per spec §18.1.

import { useEffect, useState } from 'react';
import {
  Star,
  ShoppingCart,
  Pencil,
  Cog,
  Truck,
  CheckCircle,
  FileEdit,
  ImageIcon,
  Check,
  Minus,
  Plus,
} from 'lucide-react';
import type { Design, DesignStatus } from '../lib/types';
import { fmtDate, fmtDollars, fmtQty } from '../lib/format';
import { VariantPicker, type VariantPickerSelection } from './VariantPicker';

export interface DesignCardProps {
  design: Design;
  variant?: 'full' | 'compact' | 'reorder';
  selected?: boolean;
  bulkMode?: boolean;
  onClick?: (id: string) => void;
  /** Reorder click. In the `reorder` variant, qty + variantId reflect the
   *  customer's picker selection (which may differ from the design's
   *  originally-ordered variant). In other variants both are undefined and
   *  the backend uses the design's stored variant + last_ordered_qty. */
  onReorder?: (id: string, qty?: number, variantId?: number) => void;
  onEditCopy?: (id: string) => void;
  onPinToggle?: (id: string) => void;
  onSelect?: (id: string) => void;
}

const STATUS_LABELS: Record<DesignStatus, { label: string; pillClass: string; Icon: typeof Cog }> = {
  draft:         { label: 'Draft',         pillClass: 'pill--draft',       Icon: FileEdit },
  ordered:       { label: 'Ordered',       pillClass: 'pill--ordered',     Icon: CheckCircle },
  in_production: { label: 'In production', pillClass: 'pill--production',  Icon: Cog },
  shipped:       { label: 'Shipped',       pillClass: 'pill--shipped',     Icon: Truck },
  delivered:     { label: 'Delivered',     pillClass: 'pill--delivered',   Icon: CheckCircle },
  cancelled:     { label: 'Cancelled',     pillClass: 'pill--cancelled',   Icon: FileEdit },
};

export function DesignCard({
  design,
  variant = 'full',
  selected = false,
  bulkMode = false,
  onClick,
  onReorder,
  onEditCopy,
  onPinToggle,
  onSelect,
}: DesignCardProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const status = STATUS_LABELS[design.status];
  const lastQty = design.rollups.last_ordered_qty;
  const lastDate = design.rollups.last_ordered_at;
  const orderName = design.rollups.last_ordered_order_name;
  const totalOrders = design.rollups.total_orders;
  const unitPrice = design.rollups.last_ordered_unit_price;

  // "Reorder" implies a prior purchase. For drafts (never ordered) the
  // copy on the primary CTA is "Order" instead — same backend call, more
  // honest label.
  const isDraft = design.status === 'draft';

  // Variant picker state for the 'reorder' variant. Drives the Reorder
  // button's qty/price/variant_id. Null until the picker resolves a real
  // variant or the picker decides the product has no options to surface.
  // `pickerReady` is null while loading, true if the picker rendered, false
  // if the product has no options (we fall back to a simple Reorder).
  const [pickerReady, setPickerReady] = useState<boolean | null>(null);
  const [picked, setPicked] = useState<VariantPickerSelection | null>(null);

  // Cart-qty stepper — only relevant when the picker resolves to a variant
  // whose product has NO Quantity option group (Model B/C). For Model A
  // products the picker's qty IS the cart qty; this stepper is hidden.
  // Defaults to last_ordered_qty for one-click "same as last time."
  const [cartQty, setCartQty] = useState<number>(lastQty ?? 1);
  // Re-seed cartQty if the picker resolves with a different last-time hint
  // (e.g. design rolled up from a recent order during this session).
  useEffect(() => {
    if (lastQty != null) setCartQty(lastQty);
  }, [lastQty]);

  const showThumb = design.thumbnail.url && !imgFailed;

  return (
    <article
      data-design-id={design.id}
      className={`d-card${selected ? ' is-selected' : ''}${design.is_pinned ? ' is-pinned' : ''}${bulkMode ? ' is-bulk-mode' : ''}`}
      onClick={(e) => {
        if (bulkMode && onSelect) {
          // Range select with shift-click (selects all visible designs
          // between the most-recently-clicked and this one). Done in pure
          // DOM since the store doesn't track an "anchor" id.
          if (e.shiftKey) {
            const grid = (e.currentTarget as HTMLElement).closest('.my-designs__grid');
            const ids = Array.from(grid?.querySelectorAll<HTMLElement>('[data-design-id]') ?? [])
              .map((el) => el.dataset.designId)
              .filter((s): s is string => !!s);
            const lastSelected = (window as unknown as { __mdLastSelectedDesignId?: string })
              .__mdLastSelectedDesignId;
            if (lastSelected && ids.includes(lastSelected)) {
              const a = ids.indexOf(lastSelected);
              const b = ids.indexOf(design.id);
              const [from, to] = a < b ? [a, b] : [b, a];
              for (let i = from; i <= to; i += 1) {
                onSelect(ids[i]!);
              }
            } else {
              onSelect(design.id);
            }
          } else {
            onSelect(design.id);
          }
          (window as unknown as { __mdLastSelectedDesignId?: string }).__mdLastSelectedDesignId = design.id;
        } else if (onClick) {
          onClick(design.id);
        }
      }}
      role="article"
      aria-label={design.name ?? 'Design'}
      aria-pressed={bulkMode ? selected : undefined}
    >
      {/* Ship 6: always-visible select checkbox in the top-left of the card.
          Low opacity by default (so it doesn't compete with the design),
          full opacity on hover or when the card is selected. Click enters
          bulk mode + selects this card without having to find the toolbar
          "Select" pill first. Stops click propagation so it doesn't also
          trigger the card's onClick (which would navigate / re-toggle). */}
      {onSelect && (
        <button
          type="button"
          className={`d-card__select${selected ? ' is-on' : ''}${bulkMode ? ' is-bulk' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(design.id);
          }}
          aria-label={selected ? 'Deselect' : 'Select'}
          aria-pressed={selected}
        >
          {selected ? <Check size={13} strokeWidth={2.5} /> : null}
        </button>
      )}
      <div className="d-card__thumb">
        {showThumb ? (
          <img
            src={design.thumbnail.url ?? ''}
            alt={`Preview of ${design.name ?? 'design'}`}
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="d-card__thumb-fallback" aria-hidden="true">
            <ImageIcon size={36} strokeWidth={1.4} />
            <span>No preview</span>
          </div>
        )}

        {/* For any card representing a successfully-ordered design, show
            WHEN the customer last ordered it ("Last Ordered · Apr 27, 2026")
            rather than the fulfillment stage ("ORDERED" / "DELIVERED" /
            etc). The library view (My Designs) and Quick Reorder both use
            this pill — the customer's primary need is "do I need more?"
            and the date is the relevant signal.

            Exceptions (fall back to status pill):
              - draft: lastDate is null naturally, no order to reference
              - cancelled: lastDate exists but order was voided — showing
                "Last Ordered" would falsely imply a successful purchase */}
        {lastDate && design.status !== 'cancelled' ? (
          <span className="pill pill--ordered d-card__status">
            <CheckCircle size={11} strokeWidth={1.6} aria-hidden="true" />
            Last Ordered · {fmtDate(lastDate)}
          </span>
        ) : (
          <span className={`pill ${status.pillClass} d-card__status`}>
            <status.Icon size={11} strokeWidth={1.6} aria-hidden="true" />
            {status.label}
          </span>
        )}

        {/* Pin star stays visible always; in bulk mode it stops responding
            to clicks so the card's primary interaction is selection. The
            corner checkbox above replaces the old "checkbox only in bulk
            mode" pattern, so the duplicate inside-the-thumb checkbox can
            be removed entirely — one source of truth. */}
        {onPinToggle && (
          <button
            type="button"
            className={`d-card__pin${design.is_pinned ? ' is-on' : ''}${bulkMode ? ' is-bulk' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (bulkMode) return;
              onPinToggle(design.id);
            }}
            aria-label={design.is_pinned ? 'Unpin' : 'Pin'}
            aria-disabled={bulkMode}
            tabIndex={bulkMode ? -1 : 0}
          >
            <Star size={16} fill={design.is_pinned ? 'currentColor' : 'none'} strokeWidth={1.6} />
          </button>
        )}
      </div>

      <div className="d-card__body">
        <h4 className="d-card__title">
          {design.name ?? 'Untitled design'}
          {/* Ship 5c: variants badge. Surfaces the rolled-up fork count on
              root designs that have personalized versions underneath them.
              Click bubbles up to the card click; the drawer is the place
              where a customer actually drills into the variants list, so
              the badge is informational here, not interactive. */}
          {design.variants_count != null && design.variants_count > 0 && (
            <span
              className="d-card__variants-badge"
              aria-label={`${design.variants_count} personalized version${design.variants_count === 1 ? '' : 's'}`}
              title={`${design.variants_count} personalized version${design.variants_count === 1 ? '' : 's'}`}
            >
              {design.variants_count} version{design.variants_count === 1 ? '' : 's'}
            </span>
          )}
        </h4>
        {/* The reorder variant communicates the date in the status pill
            ("Last Ordered · Apr 27, 2026") and the qty in the stepper, so
            the meta line is redundant there and gets removed entirely.

            Other variants build a meta line from a list of segments:
              - product_title (only if it differs from the design name —
                otherwise it's a duplicate of what's in the title above)
              - order frequency (only if total_orders >= 1 — drafts have
                never been ordered, so claiming "Ordered once" was a bug)
              - last ordered date + qty (only if we have those values)

            If no segments survive, the whole meta line is omitted (e.g.
            a fresh draft just shows the title with no extra noise). */}
        {variant !== 'reorder' && (() => {
          const titleDiffersFromProduct =
            design.shopify.product_title &&
            design.shopify.product_title !== design.name;
          const orderFreqText =
            totalOrders > 1
              ? `Ordered ${totalOrders} times`
              : totalOrders === 1
                ? 'Ordered once'
                : null;
          const segments: string[] = [];
          if (titleDiffersFromProduct && design.shopify.product_title) {
            segments.push(design.shopify.product_title);
          }
          if (orderFreqText) segments.push(orderFreqText);
          if (lastDate) {
            segments.push(
              `Last: ${fmtDate(lastDate)}${lastQty != null ? ` (${fmtQty(lastQty)} qty)` : ''}`,
            );
          }
          if (segments.length === 0) return null;
          return <p className="d-card__meta">{segments.join(' · ')}</p>;
        })()}
        {orderName && variant !== 'reorder' && (
          <p className="d-card__order-link">
            <span>Order {orderName}</span>
          </p>
        )}

        {variant === 'reorder' ? (
          <div className="d-card__actions d-card__actions--stack">
            {/* Variant picker — Material/Color/Quantity dropdowns. Renders
                nothing if the product has no option groups (single-SKU
                products fall through to the default reorder behavior). */}
            <VariantPicker
              designId={design.id}
              lastOrderedQty={lastQty}
              onChange={setPicked}
              onReady={setPickerReady}
            />

            {/* Cart-qty stepper — only shown when the product does NOT
                encode qty as a variant option (Model B/C). For Model A
                the picker's Quantity dropdown IS the qty selector. */}
            {(() => {
              const showStepper =
                pickerReady === false ||
                (pickerReady === true && picked != null && !picked.qtyFromPicker);
              if (!showStepper) return null;
              return (
                <div
                  className="d-card__qty"
                  onClick={(e) => e.stopPropagation()}
                  role="group"
                  aria-label="Cart quantity"
                >
                  <button
                    type="button"
                    className="d-card__qty-btn"
                    aria-label="Decrease quantity"
                    onClick={() => setCartQty((q) => Math.max(1, q - 1))}
                    disabled={cartQty <= 1}
                  >
                    <Minus size={14} strokeWidth={2} aria-hidden="true" />
                  </button>
                  <input
                    type="number"
                    className="d-card__qty-input"
                    min={1}
                    max={10000}
                    value={cartQty}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (Number.isFinite(n)) setCartQty(Math.max(1, Math.min(10000, n)));
                    }}
                    aria-label="Quantity"
                  />
                  <button
                    type="button"
                    className="d-card__qty-btn"
                    aria-label="Increase quantity"
                    onClick={() => setCartQty((q) => Math.min(10000, q + 1))}
                  >
                    <Plus size={14} strokeWidth={2} aria-hidden="true" />
                  </button>
                </div>
              );
            })()}

            <button
              type="button"
              className="btn btn--hero d-card__btn-reorder-hero"
              onClick={(e) => {
                e.stopPropagation();
                // Resolve final qty + variant_id in priority order:
                //   - Picker has Quantity option → picker's qty + variant_id
                //   - Picker has other options (Holes/Color) → cartQty + variant_id
                //   - No picker / single-SKU → cartQty + design's default variant
                if (picked) {
                  const finalQty = picked.qtyFromPicker ? picked.qty : cartQty;
                  onReorder?.(design.id, finalQty, picked.variant_id);
                } else if (pickerReady === false) {
                  onReorder?.(design.id, cartQty);
                } else {
                  // Picker hasn't resolved yet (loading or out-of-stock combo)
                  onReorder?.(design.id);
                }
              }}
              disabled={pickerReady === true && picked != null && !picked.available}
            >
              <ShoppingCart size={16} aria-hidden="true" strokeWidth={1.6} />
              <span>
                {(() => {
                  // Compose the Reorder button label.
                  //
                  // Three pricing models in MD's catalog produce three label
                  // shapes (we suppress redundant "$X.XX each" when qty=1):
                  //
                  //   Model A — Quantity option group on product (variant
                  //     encodes qty + tier price):
                  //       qty 1   → "Reorder 1 for $9.50"
                  //       qty 9   → "Reorder 9 for $67.50 ($7.50 each)"
                  //
                  //   Model B — Other options (Material/Holes/Color) but no
                  //     Quantity option (cartQty is a multiplier):
                  //       qty 1   → "Reorder 1 for $9.50"
                  //       qty 5   → "Reorder 5 for $47.50 ($9.50 each)"
                  //
                  //   Model C / no options — fallback:
                  //       qty 1   → "Reorder 1"
                  //       qty 3   → "Reorder 3"
                  if (picked) {
                    const qty = picked.qtyFromPicker ? picked.qty : cartQty;
                    const total = picked.qtyFromPicker
                      ? picked.price
                      : picked.price * cartQty;
                    const perUnit = qty > 0 ? total / qty : total;
                    if (qty <= 1) {
                      return `Reorder ${fmtQty(qty)} for ${fmtDollars(total)}`;
                    }
                    return `Reorder ${fmtQty(qty)} for ${fmtDollars(total)} (${fmtDollars(perUnit)} each)`;
                  }
                  if (pickerReady === true) {
                    return 'Pick options to reorder';
                  }
                  // Fallback for products with no option groups at all.
                  const qty = cartQty;
                  if (unitPrice != null) {
                    if (qty <= 1) return `Reorder ${fmtQty(qty)} for ${fmtDollars(unitPrice)}`;
                    return `Reorder ${fmtQty(qty)} for ${fmtDollars(unitPrice * qty)} (${fmtDollars(unitPrice)} each)`;
                  }
                  return `Reorder ${fmtQty(qty)}`;
                })()}
              </span>
            </button>
            {onEditCopy && (
              <button
                type="button"
                className="btn btn--ghost d-card__btn-edit-link"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditCopy(design.id);
                }}
              >
                or edit a copy first →
              </button>
            )}
          </div>
        ) : (
          <div className="d-card__actions">
            {onReorder && (
              <button
                type="button"
                className="btn btn--primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onReorder(design.id);
                }}
              >
                <ShoppingCart size={16} aria-hidden="true" strokeWidth={1.6} />
                <span>{isDraft ? 'Order' : 'Reorder'}</span>
              </button>
            )}
            {onEditCopy && (
              <button
                type="button"
                className="btn btn--secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditCopy(design.id);
                }}
              >
                <Pencil size={16} aria-hidden="true" strokeWidth={1.6} />
                <span>Edit a copy</span>
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
